import { useEffect, useState } from 'react'
import { Link } from 'react-router'
import { Capacitor } from '@capacitor/core'
import { parentApi, type ParentChild } from '@/lib/parent-api'
import { notificationsApi, type NotificationPreference } from '@/lib/notifications-api'
import { usePushNotifications } from '@/hooks/usePushNotifications'
import { useAppLockStore } from '@/stores/app-lock.store'
import { appLockService } from '@/lib/app-lock'
import { trackEvent, Events } from '@/lib/observability'
import { PinSetup } from '@/components/lock'

interface ChildPreference extends NotificationPreference {
  childName: string
}

export function NotificationSettingsPage() {
  const { isRegistered, permissionStatus, error: pushError, register } = usePushNotifications()

  const [children, setChildren] = useState<ParentChild[]>([])
  const [preferences, setPreferences] = useState<ChildPreference[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [savingChildId, setSavingChildId] = useState<string | null>(null)
  const [showPinSetup, setShowPinSetup] = useState(false)
  const [showChangePinSetup, setShowChangePinSetup] = useState(false)

  // App Lock state
  const {
    biometricEnabled,
    biometricAvailable,
    biometryType,
    lockTimeout,
    isSetupComplete,
    setBiometricEnabled,
    setLockTimeout,
  } = useAppLockStore()

  const biometryName = appLockService.getBiometryTypeName(biometryType)
  const [biometricError, setBiometricError] = useState<string | null>(null)

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      setIsLoading(true)
      setError(null)

      const [childrenRes, prefsRes] = await Promise.all([
        parentApi.getChildren(),
        notificationsApi.getPreferences(),
      ])

      setChildren(childrenRes.children)

      // Merge preferences with child names
      const merged = childrenRes.children.map((child) => {
        const pref = prefsRes.find((p) => p.childId === child.id)
        return {
          childId: child.id,
          childName: `${child.firstName} ${child.lastName}`,
          pushEnabled: pref?.pushEnabled ?? true,
          quietHoursStart: pref?.quietHoursStart ?? null,
          quietHoursEnd: pref?.quietHoursEnd ?? null,
        }
      })

      setPreferences(merged)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load settings')
    } finally {
      setIsLoading(false)
    }
  }

  const handleTogglePush = async (childId: string, enabled: boolean) => {
    setSavingChildId(childId)
    try {
      await notificationsApi.updatePreferences(childId, { pushEnabled: enabled })
      trackEvent(Events.NOTIFICATION_SETTINGS_CHANGED, { pushEnabled: enabled })
      setPreferences((prev) =>
        prev.map((p) => (p.childId === childId ? { ...p, pushEnabled: enabled } : p))
      )
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save')
    } finally {
      setSavingChildId(null)
    }
  }

  const handleQuietHoursChange = async (
    childId: string,
    field: 'quietHoursStart' | 'quietHoursEnd',
    value: string
  ) => {
    const pref = preferences.find((p) => p.childId === childId)
    if (!pref) return

    // Update local state immediately
    setPreferences((prev) =>
      prev.map((p) => (p.childId === childId ? { ...p, [field]: value || null } : p))
    )
  }

  const handleSaveQuietHours = async (childId: string) => {
    const pref = preferences.find((p) => p.childId === childId)
    if (!pref) return

    setSavingChildId(childId)
    try {
      await notificationsApi.updatePreferences(childId, {
        quietHoursStart: pref.quietHoursStart,
        quietHoursEnd: pref.quietHoursEnd,
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save')
    } finally {
      setSavingChildId(null)
    }
  }

  const handleToggleBiometric = async (enabled: boolean) => {
    setBiometricError(null)

    if (enabled) {
      // Verify biometrics work before enabling
      const success = await appLockService.authenticateWithBiometric(
        `Verify ${biometryName} to enable it for app unlock`
      )

      if (success) {
        setBiometricEnabled(true)
      } else {
        setBiometricError(`${biometryName} verification failed. Please try again.`)
      }
    } else {
      // No verification needed to disable
      setBiometricEnabled(false)
    }
  }

  if (isLoading) {
    return <div className="p-4">Loading...</div>
  }

  const isNative = Capacitor.isNativePlatform()

  return (
    <div className="p-4">
      <h1 className="mb-4 text-2xl font-bold">Notification Settings</h1>

      {error && (
        <div className="mb-4 rounded bg-red-100 p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Push Notification Status */}
      <section className="mb-6 rounded-lg border border-gray-200 p-4">
        <h2 className="mb-3 text-lg font-medium">Push Notifications</h2>

        {!isNative ? (
          <div className="text-sm text-gray-600">
            <p>Push notifications are only available on the mobile app.</p>
            <p className="mt-1 text-gray-500">
              Download the Brightseed app to receive instant updates.
            </p>
          </div>
        ) : permissionStatus === 'denied' ? (
          <div className="rounded bg-amber-50 p-3 text-sm text-amber-700">
            <p className="font-medium">Notifications are disabled</p>
            <p className="mt-1">
              Enable notifications in your device Settings to receive updates.
            </p>
          </div>
        ) : isRegistered ? (
          <div className="flex items-center gap-2 text-sm text-green-600">
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
            <span>Push notifications are enabled</span>
          </div>
        ) : (
          <div>
            <p className="mb-2 text-sm text-gray-600">
              Get notified when your childminder posts updates.
            </p>
            <button
              onClick={register}
              className="rounded bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
            >
              Enable Notifications
            </button>
            {pushError && (
              <p className="mt-2 text-sm text-red-600">{pushError}</p>
            )}
          </div>
        )}
      </section>

      {/* Per-Child Preferences */}
      {children.length > 0 && (
        <section>
          <h2 className="mb-3 text-lg font-medium">Preferences by Child</h2>
          <p className="mb-4 text-sm text-gray-500">
            Customize notification settings for each child.
          </p>

          <div className="space-y-4">
            {preferences.map((pref) => (
              <div
                key={pref.childId}
                className="rounded-lg border border-gray-200 p-4"
              >
                <div className="mb-3 flex items-center justify-between">
                  <h3 className="font-medium">{pref.childName}</h3>
                  <label className="relative inline-flex cursor-pointer items-center">
                    <input
                      type="checkbox"
                      checked={pref.pushEnabled}
                      onChange={(e) => handleTogglePush(pref.childId, e.target.checked)}
                      disabled={savingChildId === pref.childId}
                      className="peer sr-only"
                    />
                    <div className="peer h-6 w-11 rounded-full bg-gray-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-primary-600 peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:ring-2 peer-focus:ring-primary-300 peer-disabled:opacity-50"></div>
                  </label>
                </div>

                {pref.pushEnabled && (
                  <div className="border-t border-gray-100 pt-3">
                    <p className="mb-2 text-sm text-gray-600">Quiet Hours (optional)</p>
                    <p className="mb-3 text-xs text-gray-500">
                      No notifications during these hours.
                    </p>
                    <div className="flex items-center gap-2">
                      <input
                        type="time"
                        value={pref.quietHoursStart || ''}
                        onChange={(e) =>
                          handleQuietHoursChange(pref.childId, 'quietHoursStart', e.target.value)
                        }
                        onBlur={() => handleSaveQuietHours(pref.childId)}
                        className="rounded border border-gray-300 px-2 py-1 text-sm"
                        placeholder="Start"
                      />
                      <span className="text-gray-400">to</span>
                      <input
                        type="time"
                        value={pref.quietHoursEnd || ''}
                        onChange={(e) =>
                          handleQuietHoursChange(pref.childId, 'quietHoursEnd', e.target.value)
                        }
                        onBlur={() => handleSaveQuietHours(pref.childId)}
                        className="rounded border border-gray-300 px-2 py-1 text-sm"
                        placeholder="End"
                      />
                      {savingChildId === pref.childId && (
                        <span className="text-xs text-gray-400">Saving...</span>
                      )}
                    </div>
                    <p className="mt-2 text-xs text-gray-400">
                      Example: 22:00 to 07:00 for overnight quiet hours
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {children.length === 0 && (
        <div className="rounded border border-dashed border-gray-300 p-6 text-center text-gray-500">
          <p>No children linked to your account yet.</p>
          <p className="mt-1 text-sm">
            Notification preferences will appear here once you accept an invitation.
          </p>
        </div>
      )}

      {/* Profile Section */}
      <section className="mt-6 rounded-lg border border-gray-200 p-4">
        <h2 className="mb-3 text-lg font-medium">Your Profile</h2>
        <p className="mb-3 text-sm text-gray-500">
          Update your display name and change your password.
        </p>
        <Link
          to="/profile"
          className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
        >
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
          Edit Profile
        </Link>
      </section>

      {/* Security Section */}
      <section className="mt-6 rounded-lg border border-gray-200 p-4">
        <h2 className="mb-3 text-lg font-medium">Security</h2>
        <p className="mb-4 text-sm text-gray-500">
          Protect your account with a PIN or biometric authentication.
        </p>

        {/* App Lock Status - always enabled for childcare security */}
        <div className="mb-4 flex items-center justify-between">
          <div>
            <p className="font-medium">App Lock</p>
            <p className="text-sm text-gray-500">
              PIN required to open the app
            </p>
          </div>
          <span className="rounded-full bg-green-100 px-3 py-1 text-sm font-medium text-green-700">
            Always On
          </span>
        </div>

        {/* Lock Timeout */}
        {isSetupComplete && (
          <>
            <div className="mb-4 flex items-center justify-between border-t border-gray-100 pt-4">
              <div>
                <p className="font-medium">Lock after</p>
                <p className="text-sm text-gray-500">
                  Time in background before locking
                </p>
              </div>
              <select
                value={lockTimeout}
                onChange={(e) => setLockTimeout(Number(e.target.value))}
                className="rounded border border-gray-300 px-3 py-1.5 text-sm"
              >
                <option value={1}>1 minute</option>
                <option value={2}>2 minutes</option>
                <option value={5}>5 minutes</option>
                <option value={15}>15 minutes</option>
                <option value={30}>30 minutes</option>
              </select>
            </div>

            {/* Biometric Toggle - only show if available */}
            {biometricAvailable && (
              <div className="mb-4 border-t border-gray-100 pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Use {biometryName}</p>
                    <p className="text-sm text-gray-500">
                      Unlock faster with {biometryName}
                    </p>
                  </div>
                  <label className="relative inline-flex cursor-pointer items-center">
                    <input
                      type="checkbox"
                      checked={biometricEnabled}
                      onChange={(e) => handleToggleBiometric(e.target.checked)}
                      className="peer sr-only"
                    />
                    <div className="peer h-6 w-11 rounded-full bg-gray-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-primary-600 peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:ring-2 peer-focus:ring-primary-300"></div>
                  </label>
                </div>
                {biometricError && (
                  <p className="mt-2 text-sm text-red-600">{biometricError}</p>
                )}
              </div>
            )}

            {/* Change PIN */}
            <div className="border-t border-gray-100 pt-4">
              <button
                type="button"
                onClick={() => setShowChangePinSetup(true)}
                className="text-primary-600 font-medium text-sm"
              >
                Change PIN
              </button>
            </div>
          </>
        )}
      </section>

      {/* PIN Setup Modal */}
      {showPinSetup && (
        <PinSetup
          onComplete={() => setShowPinSetup(false)}
        />
      )}

      {/* Change PIN Modal */}
      {showChangePinSetup && (
        <PinSetup
          onComplete={() => setShowChangePinSetup(false)}
        />
      )}
    </div>
  )
}
